import { useInfiniteQuery, useQuery } from 'react-query';
import { notificationsAxiosApi } from 'src/Config/axiosApi';
import {
  NotificationsFilter,
  NotificationsListResponse,
} from '../types/notifications.types';

export function buildNotificationsParams(
  filter: NotificationsFilter,
  cursor?: string,
): Record<string, any> {
  const out: Record<string, any> = {};
  if (cursor) out.cursor = cursor;
  if (filter.from) out.from = filter.from;
  if (filter.to) out.to = filter.to;
  if (filter.ruleId) out.ruleId = filter.ruleId;
  if (filter.pairId) out.pairId = filter.pairId;
  if (filter.groupId) out.groupId = filter.groupId;

  // `direction` is sent directly to the backend. Older versions of
  // this hook expanded it client-side to a concrete `type[]` (with a
  // `__no_matching_types__` sentinel for the empty-intersection case);
  // the backend now understands `direction=entry|unwind|system` natively
  // and combines it with `type[]` via $and so the same intersection
  // semantics apply with a smaller query string and a single Mongo
  // predicate instead of an N-element $in.
  if (filter.direction && filter.direction !== 'all') {
    out.direction = filter.direction;
  }
  if (filter.types && filter.types.length > 0) {
    out.type = filter.types;
  }
  if (filter.excludeTypes && filter.excludeTypes.length > 0) {
    out.excludeType = filter.excludeTypes;
  }
  if (filter.platform && filter.platform !== 'all') {
    out.platform = filter.platform;
  }
  if (filter.source && filter.source !== 'all') {
    out.source = filter.source;
  }
  if (filter.severity && filter.severity !== 'all') {
    out.severity = filter.severity;
  }
  return out;
}

export function useNotifications(
  filter: NotificationsFilter,
  opts?: { enabled?: boolean },
) {
  return useInfiniteQuery<NotificationsListResponse>(
    ['notifications', filter],
    async ({ pageParam }) => {
      const params = buildNotificationsParams(filter, pageParam as string | undefined);
      const { data } = await notificationsAxiosApi.get<NotificationsListResponse>(
        '/notifications',
        { params },
      );
      return data;
    },
    {
      enabled: opts?.enabled !== false,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      // Refetch on focus is too noisy with the live WS overlay; we
      // hold the page steady once loaded, the WS keeps it fresh.
      refetchOnWindowFocus: false,
      staleTime: 5_000,
    },
  );
}

export function useNotificationTypes() {
  return useQuery<{ types: string[] }>(
    ['notifications', 'types'],
    async () => {
      const { data } = await notificationsAxiosApi.get<{ types: string[] }>(
        '/notifications/types',
      );
      return data;
    },
    { staleTime: 60 * 60 * 1000 },
  );
}
